import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { ErrorOwner, ErrorRelationState } from '../../api/api';
import { Dossier, DossierAction, DossierMoment, DossierNode, getDossier, ROLE_LABEL } from '../../api/dossier';
import { getTimeline, Timeline } from '../data/cockpit';
import { ago, money, when } from '../data/format';
import { apiError, recall, remember, track, usePoll } from '../data/usePoll';
import { useObjectTabs } from '../data/objectTabs';
import { Card, CheckTone, Empty, ErrorNotice, Icon, Loading, Mark, Owner, OwnerChip, Spinner } from '../components/ui';
import { FlowTimeline } from '../components/FlowTimeline';
import { v2 } from '../paths';
import { humanize, settlementEvents, timelineFlow } from './timelineFlow';

/** Where the object page was opened from, so "Terug" can say where it goes. */
export interface CameFrom {
    from: string;
    label: string;
}

const OWNER: Record<string, Owner> = { [ErrorOwner.Support]: 'us', [ErrorOwner.Customer]: 'cu', [ErrorOwner.Automatic]: 'au', [ErrorOwner.None]: 'au' };

// Roles that describe a document rather than travel to Exact themselves; many of them, so they stay folded.
const QUIET_ROLES = ['Line', 'Article', 'Subscription'];

const stateMark = (n: DossierNode): { tone: CheckTone; text: string } => {
    switch (n.exactState) {
        case ErrorRelationState.InExact:
            return { tone: 'ok', text: n.entryNumber ? `in Exact · ${n.entryNumber}` : 'in Exact' };
        case ErrorRelationState.Skipped:
            return { tone: 'ok', text: 'niets te boeken' };
        case ErrorRelationState.RemovedByChargeback:
            return { tone: 'warn', text: 'weggehaald na stornering' };
        case ErrorRelationState.ChargebackProcessed:
            return { tone: 'ok', text: 'stornering verwerkt' };
        case ErrorRelationState.NotInExact:
            return { tone: n.failedAttempts > 0 ? 'bad' : 'wait', text: n.failedAttempts > 0 ? `niet in Exact · ${n.failedAttempts}× mislukt` : 'nog niet in Exact' };
        default:
            return { tone: 'todo', text: 'onbekend' };
    }
};

const MOMENT: Record<string, CheckTone> = { Failed: 'bad', Succeeded: 'ok', Requeued: 'wait', Removed: 'warn', Note: 'todo' };

const worse = (a: DossierNode, b: DossierNode) => {
    const rank = (n: DossierNode) => (n.exactState === ErrorRelationState.InExact || n.exactState === ErrorRelationState.Skipped ? 0 : 1) * 1000 + n.failedAttempts;
    return rank(b) > rank(a) ? b : a;
};

/**
 * Parents before children, with the depth of each. One document the connector tracks under two object types
 * (an invoice as sales invoice and as sales entry) is one row, with the worse of the two states.
 */
const tree = (nodes: DossierNode[]) => {
    const kept = new Map<string, DossierNode>();
    const alias = new Map<string, string>();
    nodes.forEach((n) => {
        const id = `${n.role}|${n.relationId}`;
        const seen = kept.get(id);
        if (!seen) kept.set(id, n);
        else kept.set(id, worse(seen, n));
    });
    nodes.forEach((n) => alias.set(n.key, kept.get(`${n.role}|${n.relationId}`)!.key));
    const unique = Array.from(kept.values());

    const byParent = new Map<string, DossierNode[]>();
    unique.forEach((n) => {
        const parent = n.parentKey ? alias.get(n.parentKey) : undefined;
        const key = parent && parent !== n.key && unique.some((p) => p.key === parent) ? parent : '';
        byParent.set(key, [...(byParent.get(key) ?? []), n]);
    });
    const out: { node: DossierNode; depth: number }[] = [];
    const walk = (parent: string, depth: number) =>
        (byParent.get(parent) ?? []).forEach((n) => {
            out.push({ node: n, depth });
            walk(n.key, depth + 1);
        });
    walk('', 0);
    return out;
};

/** The three systems for the object in focus: where it is, at a glance. */
const Stepper = ({ node }: { node?: DossierNode }) => {
    const inExact = node?.exactState === ErrorRelationState.InExact || node?.exactState === ErrorRelationState.Skipped;
    const failing = !inExact && (node?.failedAttempts ?? 0) > 0;
    return (
        <div className="stepper" style={{ maxWidth: 520 }}>
            <span className="sp"><Mark tone="ok" />Chargebee</span>
            <span className="sl ok" />
            <span className="sp"><Mark tone={inExact ? 'ok' : failing ? 'bad' : 'wait'} />Connector</span>
            <span className={inExact ? 'sl ok' : 'sl'} />
            <span className="sp"><Mark tone={inExact ? 'ok' : 'todo'} />Exact</span>
        </div>
    );
};

/** Who has to do something, counted: one chip per owner instead of a paragraph. */
const Owners = ({ actions }: { actions: DossierAction[] }) => {
    const count = actions.reduce<Record<Owner, number>>((n, a) => ({ ...n, [OWNER[a.owner] ?? 'us']: n[OWNER[a.owner] ?? 'us'] + 1 }), { us: 0, cu: 0, au: 0 });
    return (
        <span className="hrow" style={{ gap: 6 }}>
            {(['us', 'cu', 'au'] as Owner[])
                .filter((o) => count[o] > 0)
                .map((o) => (
                    <span key={o} className="hrow" style={{ gap: 4 }}>
                        <OwnerChip owner={o} long />
                        <span className="mono mut" style={{ fontSize: 12 }}>{count[o]}</span>
                    </span>
                ))}
        </span>
    );
};

const Moment = ({ m, node, onPick }: { m: DossierMoment; node?: DossierNode; onPick: (n: DossierNode) => void }) => {
    const words = humanize(m.headline, m.detail);
    return (
    <div className="moment">
        <span className="mono mut" style={{ fontSize: 12 }}>{m.lastAt ? when(m.lastAt) : ''}</span>
        <Mark tone={MOMENT[m.kind] ?? 'todo'} />
        <div style={{ minWidth: 0 }}>
            <div>
                <b style={{ fontWeight: 500 }}>{words.title}</b>
                {m.count > 1 && <span className="chip" style={{ marginLeft: 8, padding: '0 7px' }}>{m.count}×</span>}
            </div>
            <div className="m">
                {node && (
                    <button className="linkish" onClick={() => onPick(node)}>
                        {ROLE_LABEL[node.role]} {node.relationId}
                    </button>
                )}
                {words.detail && <span>{words.detail}</span>}
            </div>
            {m.raw && m.kind === 'Failed' && (
                <details>
                    <summary className="mut" style={{ cursor: 'pointer', fontSize: 12 }}>Melding van de connector</summary>
                    <div className="codebox" style={{ marginTop: 6 }}>{m.raw}</div>
                </details>
            )}
        </div>
    </div>
    );
};

/**
 * One object, whole: whose it is, what belongs to it, where it stands in Chargebee, the connector and
 * Exact, what happened to it in order, and what has to happen next. Opened from the search and from an issue.
 */
export const ObjectPage = () => {
    const { customer = '', environment = '', objectId = '' } = useParams();
    const axios = useAxiosSwagger();
    const navigate = useNavigate();
    const location = useLocation();
    const came = location.state as CameFrom | null;
    const liveKey = `live/${customer}/${environment}/${objectId}`;
    const [live, setLive] = useState<Dossier | undefined>(() => recall<Dossier>(liveKey)?.data);
    const { open: openTab } = useObjectTabs(customer, environment);

    // Another object in the same page: its own comparison, if it had one.
    useEffect(() => setLive(recall<Dossier>(liveKey)?.data), [liveKey]);
    const [liveBusy, setLiveBusy] = useState(false);
    const [liveError, setLiveError] = useState<string>();
    const [showQuiet, setShowQuiet] = useState(false);

    const base = usePoll<Dossier>(
        axios ? (s) => getDossier(axios, customer, environment, { id: objectId }, s) : null,
        60000,
        [axios, customer, environment, objectId],
        `dossier/${customer}/${environment}/${objectId}`
    );
    const dossier = live?.query === objectId ? live : base.data;
    const focus = dossier?.nodes.find((n) => n.key === dossier.focusKey) ?? dossier?.nodes[0];
    const relation = focus?.relationId ?? objectId;
    const tabLabel = focus ? `${ROLE_LABEL[focus.role]} ${focus.relationId}` : objectId;

    // Every object opened stays a tab of its environment until it is closed.
    useEffect(() => {
        if (objectId) openTab({ id: objectId, label: tabLabel });
    }, [objectId, tabLabel, openTab]);

    const timeline = usePoll<Timeline>(
        axios ? (s) => getTimeline(axios, customer, environment, { relationId: relation }, s) : null,
        30000,
        [axios, customer, environment, relation],
        `timeline/${customer}/${environment}/${relation}`
    );
    const asked = !!dossier?.askedChargebee || !!dossier?.askedExact;
    const flow = useMemo(() => {
        if (!timeline.data || timeline.data.events.length === 0) return undefined;
        const all = dossier?.nodes ?? [];
        const settled = new Set(all.filter((n) => n.exactState === ErrorRelationState.InExact || n.exactState === ErrorRelationState.Skipped).map((n) => n.key));
        // The connector's error does not say which article Exact missed; the dossier knows the invoice's articles.
        const lineItemTitles = (dossier?.moments ?? []).filter((m) => m.interpretation?.code === 'line-item-not-found').map((m) => m.headline);
        const articlesUnder = (key: string): string[] =>
            all.filter((n) => n.parentKey === key).flatMap((n) => (n.role === 'Article' ? [n.relationId] : articlesUnder(n.key)));
        const explain = (documentId: string, title: string) => {
            if (!lineItemTitles.some((t) => title.startsWith(t))) return undefined;
            const articles = Array.from(new Set(articlesUnder(documentId)));
            return articles.length ? `artikelen op de factuur: ${articles.slice(0, 4).join(', ')}${articles.length > 4 ? ` +${articles.length - 4}` : ''}` : undefined;
        };
        return timelineFlow(timeline.data, asked ? settlementEvents(dossier?.settlements, timeline.data, dossier?.nodes) : undefined, { settled, explain });
    }, [timeline.data, dossier, asked]);

    const nodes = useMemo(() => tree(dossier?.nodes ?? []), [dossier]);
    const documents = nodes.filter((t) => !QUIET_ROLES.includes(t.node.role));
    const owner = dossier?.nodes.find((n) => n.role === 'Customer');
    const quiet = nodes.length - documents.length;
    const nodeOf = (key: string) => dossier?.nodes.find((n) => n.key === key);
    const here: CameFrom = { from: location.pathname, label: focus ? `${ROLE_LABEL[focus.role].toLowerCase()} ${focus.relationId}` : objectId };
    const open = (n: DossierNode) => n.relationId !== focus?.relationId && navigate(v2.object(customer, environment, n.relationId), { state: here });
    const settlement = dossier?.settlements?.find((s) => s.invoiceKey === focus?.key) ?? dossier?.settlements?.[0];

    const compare = async () => {
        if (!axios) return;
        setLiveBusy(true);
        setLiveError(undefined);
        try {
            const result = await track(getDossier(axios, customer, environment, { id: objectId, chargebee: true, exact: true }));
            remember(liveKey, result);
            setLive(result);
        } catch (e) {
            setLiveError(apiError(e));
        } finally {
            setLiveBusy(false);
        }
    };

    const liveButton = (
        <button className="btn sm" disabled={liveBusy} onClick={compare} title="Vraagt Chargebee en Exact zelf; kost een paar Exact-calls van de klant">
            {liveBusy && <Spinner />}
            {asked ? 'Opnieuw vragen' : 'Chargebee en Exact erbij'}
        </button>
    );

    return (
        <>
            <div className="toolbar">
                <Link className="btn sm ghost" to={came?.from ?? v2.env(customer, environment)}>
                    <Icon name="back" size={14} />
                    {came ? `Terug naar ${came.label}` : `Naar ${customer} ${environment}`}
                </Link>
                <span className="mut" style={{ fontSize: 12 }}>{base.loadedAt ? `bijgewerkt ${ago(base.loadedAt)} geleden` : ''}</span>
            </div>

            <ErrorNotice error={base.error} />
            {base.pending && <Loading>Dossier van {objectId} ophalen…</Loading>}

            {dossier && (
                <>
                    <section className="card objhead" title={dossier.conclusion}>
                        <div className="cb stack" style={{ gap: 14 }}>
                            <div className="hrow" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ minWidth: 0 }}>
                                    <div className="eyebrow">{focus ? ROLE_LABEL[focus.role] : 'Object'}</div>
                                    <div className="mono" style={{ fontSize: 24, fontWeight: 600, marginTop: 2 }}>{focus?.relationId ?? dossier.rootId}</div>
                                </div>
                                <div className="hrow" style={{ gap: 8 }}>
                                    {owner && owner.relationId !== focus?.relationId && (
                                        <button className="chip" onClick={() => open(owner)} title="Dossier van de klant openen">
                                            Klant <span className="mono">{owner.relationId}</span>
                                        </button>
                                    )}
                                    <Owners actions={dossier.actions} />
                                </div>
                            </div>
                            <Stepper node={focus} />
                            {documents.length > 1 && (
                                <div className="docstates">
                                    {documents.map(({ node }) => {
                                        const s = stateMark(node);
                                        return (
                                            <button key={node.key} className={`docstate ${s.tone} ${node.key === focus?.key ? 'on' : ''}`} onClick={() => open(node)}>
                                                <Mark tone={s.tone} />
                                                <span>
                                                    <span className="r">{ROLE_LABEL[node.role]}</span> <span className="mono">{node.relationId}</span>
                                                    <small>{s.text}</small>
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            {liveError && <div className="bad" style={{ fontSize: 13 }}>{liveError}</div>}
                        </div>
                    </section>

                    {dossier.actions.length > 0 && (
                        <Card title="Wat nu">
                            {dossier.actions.map((a) => (
                                <div key={a.code} className="dsec">
                                    <div className="hrow" style={{ gap: 8 }}>
                                        <OwnerChip owner={OWNER[a.owner] ?? 'us'} long />
                                        <b>{a.title}</b>
                                    </div>
                                    {a.action && <div style={{ marginTop: 6 }}>{a.action}</div>}
                                    {a.steps.length > 0 && (
                                        <details style={{ marginTop: 6 }}>
                                            <summary className="mut" style={{ cursor: 'pointer', fontSize: 12 }}>{a.steps.length} stappen</summary>
                                            <ol className="steps-num" style={{ marginTop: 10 }}>
                                                {a.steps.map((s, i) => (
                                                    <li key={i}><span>{s}</span></li>
                                                ))}
                                            </ol>
                                        </details>
                                    )}
                                </div>
                            ))}
                        </Card>
                    )}

                    {settlement && (
                        <Card title="Chargebee en Exact naast elkaar" right={<span className={settlement.verdict === 'InStep' ? 'chip ok' : settlement.verdict === 'Unknown' ? 'chip' : 'chip warn'}>{settlement.summary}</span>}>
                            <div className="cb g3">
                                <div><div className="eyebrow">Factuur</div><div className="mono" style={{ fontSize: 18 }}>{money(settlement.total)}</div></div>
                                <div><div className="eyebrow">Open in Chargebee</div><div className="mono" style={{ fontSize: 18 }}>{money(settlement.openInChargebee)}</div></div>
                                <div><div className="eyebrow">Open in Exact</div><div className="mono" style={{ fontSize: 18 }}>{money(settlement.openInExact)}</div></div>
                            </div>
                            {settlement.findings.length > 0 && (
                                <div className="cb checks" style={{ paddingTop: 0 }}>
                                    {settlement.findings.map((f, i) => (
                                        <div key={i} className="chk" style={{ gridTemplateColumns: '22px minmax(0,1fr)' }}>
                                            <Mark tone={f.warning ? 'warn' : 'ok'} />
                                            <span>{f.text}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    )}

                    <Card
                        title="Timeline"
                        right={
                            <span className="hrow" style={{ gap: 10 }}>
                                <span className={asked ? 'chip ok' : 'chip'}>{asked ? 'met Chargebee en Exact' : 'alleen wat de connector logde'}</span>
                                {liveButton}
                            </span>
                        }
                    >
                        <div className="cb">
                            {timeline.pending && <Loading>Timeline ophalen…</Loading>}
                            {timeline.error && <Empty>{timeline.error}</Empty>}
                            {!asked && flow && (
                                <div className="mut" style={{ fontSize: 13, marginBottom: 10 }}>
                                    De connector logt vooral wat misgaat. Aanmaken in Chargebee, betalingen en afletteren in Exact komen erbij met <b>Chargebee en Exact erbij</b>.
                                </div>
                            )}
                            {flow ? <FlowTimeline items={flow.items} documents={flow.documents} /> : timeline.data && <Empty>Geen stappen gevonden in de tabellen van de connector.</Empty>}
                        </div>
                    </Card>

                    <div className="g2" style={{ gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.4fr)' }}>
                        <Card title="Hoort bij elkaar" right={<span className="mut" style={{ fontSize: 12 }}>{documents.length} objecten</span>}>
                            {nodes
                                .filter((t) => showQuiet || !QUIET_ROLES.includes(t.node.role))
                                .map(({ node, depth }) => {
                                    const s = stateMark(node);
                                    const isFocus = node.key === focus?.key;
                                    return (
                                        <button
                                            key={node.key}
                                            className={`li ${isFocus ? 'on' : ''}`}
                                            style={{ gridTemplateColumns: '22px minmax(0,1fr)', paddingLeft: 16 + depth * 18 }}
                                            onClick={() => open(node)}
                                            title={node.linkedBy ?? undefined}
                                        >
                                            <Mark tone={s.tone} />
                                            <span>
                                                <span className="t" style={{ fontWeight: 500 }}>
                                                    {ROLE_LABEL[node.role]} <span className="mono">{node.relationId}</span>
                                                </span>
                                                <span className="m">
                                                    <span>{s.text}</span>
                                                    {!node.linkCertain && <span className="warn">verband waarschijnlijk</span>}
                                                    {node.interpretation?.title && node.exactState !== ErrorRelationState.InExact && <span className="bad">{node.interpretation.title}</span>}
                                                </span>
                                            </span>
                                        </button>
                                    );
                                })}
                            {quiet > 0 && (
                                <button className="btn sm ghost" style={{ margin: 12 }} onClick={() => setShowQuiet(!showQuiet)}>
                                    {showQuiet ? 'Regels en abonnement verbergen' : `Ook ${quiet} regels, artikelen en abonnement`}
                                </button>
                            )}
                        </Card>

                        <Card title="Wat er gebeurde" right={<span className="mut" style={{ fontSize: 12 }}>nieuwste boven</span>}>
                            {dossier.moments.length === 0 && <Empty>Niets gelogd in deze periode.</Empty>}
                            {[...dossier.moments]
                                .sort((a, b) => (b.lastAt ?? '').localeCompare(a.lastAt ?? ''))
                                .map((m) => (
                                    <Moment key={m.id} m={m} node={nodeOf(m.nodeKey)} onPick={open} />
                                ))}
                        </Card>
                    </div>

                    {dossier.gaps.length > 0 && (
                        <Card title="Niet te zien">
                            <div className="cb checks">
                                {dossier.gaps.map((g, i) => (
                                    <div key={i} className="chk" style={{ gridTemplateColumns: '22px minmax(0,1fr)' }}>
                                        <Mark tone="todo" />
                                        <span className="mut">{g}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                </>
            )}
        </>
    );
};
